import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Columns3 } from "lucide-react";
import { CRITERIA, STYLES, INSTRUMENT_GROUPS, DEPOSIT_RANGES } from "@/data/criteria";
import { COUNTRIES, PLATFORMS } from "@/data/brokers";
import { rankBrokers } from "@/lib/matching";
import { Eyebrow } from "@/components/shared/Primitives";
import { BrokerResult } from "@/components/brokers/BrokerResult";
import { useCompare } from "@/context/CompareContext";
import { useBrokers } from "@/context/BrokersContext";

const ease = [0.2, 0.8, 0.2, 1];
const STEPS = ["Priorities", "Specifications", "Matches"];

const Pills = ({ options, value, onChange, multi = false, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {options.map((o) => {
      const on = multi ? value.includes(o) : value === o;
      return <button key={o} data-on={on} onClick={() => onChange(multi ? (on ? value.filter((v) => v !== o) : [...value, o]) : o)} className="pill" data-testid={`${testid}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{o}</button>;
    })}
  </div>
);

export default function Finder() {
  const { brokers } = useBrokers();
  const [step, setStep] = useState(0);
  const [criteria, setCriteria] = useState({ rawSpreads: "preference", reliability: "essential" });
  const [country, setCountry] = useState("");
  const [platform, setPlatform] = useState("No preference");
  const [instruments, setInstruments] = useState(["Forex"]);
  const [style, setStyle] = useState("Swing trading");
  const [deposit, setDeposit] = useState("$500 – $2,000");
  const [maxCost, setMaxCost] = useState(12);
  const { slugs } = useCompare();

  const results = useMemo(() => rankBrokers({ criteria, country, platform, deposit }, undefined, brokers).filter((r) => r.broker.eurUsdSpread * 10 + r.broker.commission <= maxCost || maxCost >= 20).slice(0, 5), [brokers, criteria, country, platform, deposit, maxCost]);

  const cycle = (id) => setCriteria((c) => {
    const n = { ...c };
    if (!n[id]) n[id] = "preference"; else if (n[id] === "preference") n[id] = "essential"; else delete n[id];
    return n;
  });

  return (
    <div className="container-x py-12 lg:py-16" data-testid="finder-page">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
        <aside className="lg:col-span-3">
          <div className="lg:sticky lg:top-24">
            <Eyebrow className="mb-4">Broker finder</Eyebrow>
            <h1 className="t-h3">Tell us how you trade.</h1>
            <p className="mt-3 text-sm text-[var(--ink-3)]">Three short steps. Results update live — no page reloads, no long forms.</p>
            <ol className="mt-8 space-y-1">
              {STEPS.map((s, i) => (
                <li key={s}>
                  <button onClick={() => setStep(i)} data-testid={`finder-step-${i}`} className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-colors ${step === i ? "bg-white text-[var(--ink)] shadow-sm" : "text-[var(--ink-3)] hover:text-[var(--ink)]"}`}>
                    <span className={`flex h-6 w-6 items-center justify-center rounded-full font-mono text-[11px] ${step >= i ? "bg-[var(--cobalt)] text-white" : "bg-[var(--line)] text-[var(--ink-3)]"}`}>{i + 1}</span>{s}
                  </button>
                </li>
              ))}
            </ol>
            <div className="card mt-8 p-5 text-xs leading-relaxed text-[var(--ink-3)]">
              <p className="eyebrow mb-2">Your profile</p>
              <p>{Object.keys(criteria).length} priorities · {country || "Any country"} · {platform} · {style}</p>
              <p className="mt-1">{deposit}{maxCost < 20 ? ` · max ~$${maxCost}/lot all-in` : ""}</p>
            </div>
          </div>
        </aside>

        <div className="lg:col-span-9">
          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.section key="s0" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.45, ease }} data-testid="finder-step-priorities">
                <h2 className="t-h4">Select your trading priorities</h2>
                <p className="mt-2 text-sm text-[var(--ink-3)]">Tap once for a preference, twice to mark it essential. Essentials carry double weight and heavily penalise brokers that miss them.</p>
                <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {CRITERIA.map((c, i) => {
                    const lvl = criteria[c.id]; const Icon = c.icon;
                    return (
                      <button key={c.id} onClick={() => cycle(c.id)} data-level={lvl || "off"} data-testid={`finder-criterion-${c.id}`}
                        className="card card-tap flex flex-col p-5 text-left lg:p-6">
                        <span className="flex items-start justify-between gap-3">
                          <span className={`eyebrow ${lvl ? "text-[var(--cobalt)]" : ""}`}>0{i + 1} · {c.short}</span>
                          <span className="flex items-center gap-2.5">
                            {lvl && <span className="eyebrow text-[var(--cobalt)]">{lvl === "essential" ? "Essential" : "Preference"}</span>}
                            <Icon size={16} strokeWidth={1.6} className={lvl ? "text-[var(--cobalt)]" : "text-[var(--ink-4)]"} />
                          </span>
                        </span>
                        <span className={`mt-8 block card-value ${lvl ? "text-[var(--cobalt)]" : ""}`}>{c.label}</span>
                        <span className="mt-2 block text-sm leading-relaxed text-[var(--ink-3)]">{c.hint}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="mt-10 flex justify-end"><button onClick={() => setStep(1)} className="btn-primary" data-testid="finder-next-1">Continue <ArrowRight size={16} /></button></div>
              </motion.section>
            )}

            {step === 1 && (
              <motion.section key="s1" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.45, ease }} className="space-y-10" data-testid="finder-step-specs">
                <div>
                  <h2 className="t-h4">Additional specifications</h2>
                  <p className="mt-2 text-sm text-[var(--ink-3)]">Country matters most: bonuses, leverage and account products differ across legal entities.</p>
                </div>
                <label className="block max-w-sm">
                  <span className="eyebrow">Country of residence</span>
                  <select value={country} onChange={(e) => setCountry(e.target.value)} className="control mt-2 w-full" data-testid="finder-country">
                    <option value="">Any / prefer not to say</option>
                    {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </label>
                <div><span className="eyebrow block mb-3">Preferred platform</span><Pills options={PLATFORMS} value={platform} onChange={setPlatform} testid="finder-platform" /></div>
                <div><span className="eyebrow block mb-3">Instruments traded</span><Pills options={INSTRUMENT_GROUPS} value={instruments} onChange={setInstruments} multi testid="finder-instruments" /></div>
                <div><span className="eyebrow block mb-3">Trading style</span><Pills options={STYLES} value={style} onChange={setStyle} testid="finder-style" /></div>
                <div><span className="eyebrow block mb-3">Approximate deposit</span><Pills options={DEPOSIT_RANGES} value={deposit} onChange={setDeposit} testid="finder-deposit" /></div>
                <div className="max-w-md">
                  <div className="flex items-center justify-between"><span className="eyebrow">Max all-in cost per lot (optional)</span><span className="font-mono text-sm">{maxCost >= 20 ? "No limit" : `$${maxCost}`}</span></div>
                  <input type="range" min="4" max="20" step="1" value={maxCost} onChange={(e) => setMaxCost(+e.target.value)} className="mt-3 w-full accent-[var(--cobalt)]" data-testid="finder-max-cost" />
                </div>
                <div className="flex justify-between"><button onClick={() => setStep(0)} className="btn-ghost"><ArrowLeft size={16} /> Back</button><button onClick={() => setStep(2)} className="btn-primary" data-testid="finder-show-matches">Show matching brokers <ArrowRight size={16} /></button></div>
              </motion.section>
            )}

            {step === 2 && (
              <motion.section key="s2" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.45, ease }} data-testid="finder-step-results">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <h2 className="t-h4">{results.length} matching broker{results.length === 1 ? "" : "s"}</h2>
                    <p className="mt-2 text-sm text-[var(--ink-3)]">Ranked by transparent compatibility. Select up to three to compare side by side.</p>
                  </div>
                  <Link to="/brokers/compare" className={`btn-outline ${slugs.length ? "" : "pointer-events-none opacity-50"}`} data-testid="finder-go-compare"><Columns3 size={15} /> Compare ({slugs.length}/3)</Link>
                </div>
                <div className="mt-8 space-y-4">
                  <AnimatePresence>{results.map((r, i) => <BrokerResult key={r.broker.slug} result={r} index={i} />)}</AnimatePresence>
                  {results.length === 0 && <div className="card card-plain p-10 text-center text-sm text-[var(--ink-3)]" data-testid="finder-empty">No broker in our panel serves that combination. Relax the cost limit or essentials.</div>}
                </div>
                <p className="mt-6 text-xs leading-relaxed text-[var(--ink-4)]"><strong className="text-[var(--ink-3)]">Affiliate disclosure.</strong> We may earn a commission when you register through our links. Match scores are computed from published, verifiable conditions and are never adjusted for commercial terms.</p>
                <div className="mt-8"><button onClick={() => setStep(1)} className="btn-ghost"><ArrowLeft size={16} /> Adjust specifications</button></div>
              </motion.section>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
